// ImpactCriteriaDialog.jsx
import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
} from "@mui/material";

const ImpactCriteriaDialog = ({ open, onClose }) => {
  const columns = [
    { level: 1, label: "Negligible", bg: "#9CCC65" },
    { level: 2, label: "Low",         bg: "#4CAF50" },
    { level: 3, label: "Moderate",    bg: "#FFC107" },
    { level: 4, label: "High",        bg: "#F44336" },
    { level: 5, label: "Extreme",     bg: "#6A1B9A" },
  ];

  const rows = [
    {
      category: "Financial (Cost)",
      descriptions: [
        "Cost increase <1% of project budget; no impact on funding or contingency.",
        "Cost increase 1–3% of project budget; small impact on contingency but manageable.",
        "Cost increase 3–5%; requires budget realignment but no major overruns.",
        "Cost increase 5–10%; requires external funding approval or major budget adjustments.",
        "Cost increase >10%; risk of financial crisis or potential project cancellation.",
      ],
    },
    {
      category: "Schedule (Time)",
      descriptions: [
        "Delay <2 weeks with no effect on milestones.",
        "Delay of 2–4 weeks, manageable within float; no impact on critical path.",
        "Delay of 1–3 months affecting milestones but still recoverable.",
        "Delay of 3–6 months impacting critical milestones; acceleration plans required.",
        "Delay >6 months, severely affecting project delivery and operational readiness.",
      ],
    },
    {
      category: "Quality",
      descriptions: [
        "Minor deviations from specifications; easily corrected with no rework needed.",
        "Moderate quality issues requiring minor rework; no impact on operations.",
        "Significant quality issues requiring major rework, still correctable within time and budget.",
        "Major quality failures affecting system performance or safety; extensive rework required.",
        "Irreversible quality failure rendering key components unusable; project objectives may not be met.",
      ],
    },
    {
      category: "Safety",
      descriptions: [
        "Insignificant; no injuries or incidents; minor first-aid case.",
        "Minor injury requiring medical treatment; no lost-time injury.",
        "Serious injury resulting in lost workdays or temporary work stoppage.",
        "Severe injury or permanent disability; likely regulatory investigation.",
        "Fatality or multiple severe injuries; triggers government / regulator intervention.",
      ],
    },
    {
      category: "Scope",
      descriptions: [
        "Minor changes not impacting cost and/or schedule.",
        "Major scope changes not significantly impacting cost or schedule.",
        "Major scope changes impacting cost and/or schedule; needs additional approvals and redesign.",
        "Critical scope changes requiring redesign and contract amendments; major management intervention.",
        "Fundamental failure to meet project objectives; complete re-design or cancellation required.",
      ],
    },
    {
      category: "Regulatory & Legal",
      descriptions: [
        "No legal violations; minor compliance issue with no penalties.",
        "Minor non-compliance issue resulting in a warning or minor corrective action.",
        "Regulatory violation leading to penalties or temporary work suspension.",
        "Major legal action, permit suspension, or substantial financial penalties.",
        "Project shutdown, major lawsuits, criminal charges, or government intervention.",
      ],
    },
    {
      category: "Weather & Force Majeure",
      descriptions: [
        "Minor weather events; <1 day delay with no impact on operations.",
        "Seasonal weather variations causing <1 week delay; manageable within project float.",
        "Unforeseeable weather events causing 1–2 weeks delay; minor milestone impact but manageable.",
        "Major weather events causing delays of 2–4 weeks; schedule adjustments required.",
        "Severe weather or force majeure events causing delays >4 weeks; severe impact on delivery and safety.",
      ],
    },
    {
      category: "Supply Chain Logistics",
      descriptions: [
        "Minor delays in delivery; <1% impact on schedule.",
        "Low-impact delays affecting non-critical materials; <1 week delay, manageable within project.",
        "Moderate delays (1–3 weeks) in significant materials affecting some milestones.",
        "Major delays (3–6 weeks) in critical materials impacting milestones; urgent action required.",
        "Severe delays (>6 weeks) in essential components causing major disruptions or phase cancellation.",
      ],
    },
    {
      category: "Reputational",
      descriptions: [
        "No public / media attention; no stakeholder concerns.",
        "Localised stakeholder concerns; minor negative mentions in local media.",
        "Negative local media coverage; moderate stakeholder dissatisfaction.",
        "National media coverage; significant reputational damage and public scrutiny.",
        "International media attention; severe damage to credibility and public trust.",
      ],
    },
  ];

  const likelihoodLegend = [
    "1 – Rare",
    "2 – Not Likely",
    "3 – Likely",
    "4 – Highly Expected",
    "5 – Expected (almost certain)",
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ fontFamily: "Outfit", fontWeight: 600 }}>
        Impact Criteria &amp; Threat Criteria
      </DialogTitle>

      <DialogContent dividers>
        <Typography
          sx={{
            mb: 2,
            fontSize: "14px",
            fontFamily: "Outfit",
            color: "#4B5563",
          }}
        >
          Use this matrix when assigning <strong>Impact (1–5)</strong> to a risk.
          Each row describes the effect on a specific impact category, from{" "}
          <strong>Negligible</strong> to <strong>Extreme</strong>.
        </Typography>

        {/* IMPACT MATRIX */}
        <Box sx={{ overflowX: "auto" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "190px repeat(5, minmax(190px, 1fr))",
              border: "1px solid #E5E9EB",
              borderRadius: "8px",
              overflow: "hidden",
              minWidth: "900px",
            }}
          >
            {/* Top header row */}
            <Box
              sx={{
                bgcolor: "#111827",
                color: "#FFFFFF",
                p: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 600,
                fontSize: "13px",
              }}
            >
              Risk Impact Category
            </Box>

            {columns.map((col) => (
              <Box
                key={col.level}
                sx={{
                  p: 1,
                  borderLeft: "1px solid #E5E9EB",
                  bgcolor: col.bg,
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{ fontWeight: 700, fontSize: "14px", color: "#111827" }}
                >
                  {col.level}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "12px",
                    color: "#111827",
                  }}
                >
                  {col.label}
                </Typography>
              </Box>
            ))}

            {/* Rows */}
            {rows.map((row) => (
              <React.Fragment key={row.category}>
                {/* Category cell */}
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: "#111827",
                    color: "#FFFFFF",
                    borderTop: "1px solid #374151",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    fontWeight: 600,
                    fontSize: "13px",
                  }}
                >
                  {row.category}
                </Box>

                {/* Description cells */}
                {row.descriptions.map((text, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      p: 1.5,
                      borderTop: "1px solid #E5E9EB",
                      borderLeft: "1px solid #E5E9EB",
                      bgcolor: "#FFFFFF",
                    }}
                  >
                    <Typography sx={{ fontSize: "12px", color: "#111827" }}>
                      {text}
                    </Typography>
                  </Box>
                ))}
              </React.Fragment>
            ))}
          </Box>
        </Box>

        {/* THREAT / LIKELIHOOD LEGEND */}
        <Box sx={{ mt: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              mb: 1,
              fontFamily: "Outfit",
              fontSize: "14px",
            }}
          >
            Threat (Likelihood) Levels
          </Typography>
          <Typography sx={{ fontSize: "12px", color: "#4B5563" }}>
            When you select <strong>Likelihood</strong> for a risk, use:
          </Typography>
          <Box sx={{ mt: 1, pl: 2 }}>
            {likelihoodLegend.map((item) => (
              <Typography
                key={item}
                sx={{ fontSize: "12px", color: "#4B5563" }}
              >
                • {item}
              </Typography>
            ))}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          sx={{ textTransform: "none", fontFamily: "Outfit" }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImpactCriteriaDialog;
